/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class TemplatesController extends Controller {
  @service
  session;

  @service
  store;

  PersonType = PersonType;
  personTypes = PersonType.values;

  @tracked
  templateName = '';

  @action
  setTemplateName(value) {
    this.templateName = value;
  }

  @tracked
  templateKey = '';

  @action
  setTemplateKey(value) {
    this.templateKey = value;
  }

  @tracked
  templateDescription = '';

  @action
  setTemplateDescription(value) {
    this.templateDescription = value;
  }

  @tracked
  templateId = '';

  @tracked
  templatePersonType = 0;

  @action
  setTemplatePersonType(value) {
    this.templatePersonType = value;
  }

  @tracked
  templateSequence = 9999;

  @action
  setTemplateSequence(value) {
    this.templateSequence = value;
  }

  @tracked
  templateDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteTemplateDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  @tracked
  selectedTab = PersonType.LEARNER;

  @action
  setSelectedTab(value) {
    this.selectedTab = value;
  }

  // toggleDialog opens the template dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('templateDialogOpen');
    if (!this.templateDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleTemplateDialog() {
    this.toggleDialog();
  }

  @action
  editTemplate(id) {
    this.store.findRecord('people-schema/set', id).then((template) => {
      let personType = findBy(
        this.personTypes,
        'value',
        template.get('personType'),
      );
      this.templateName = template.get('name');
      this.templateKey = template.get('key');
      this.templateDescription = template.get('description');
      this.templatePersonType = personType;
      this.templateSequence = template.get('sequence');
    });
    this.templateId = id;
    this.toggleDialog();
  }

  @action
  saveTemplate() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.templateId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('people-schema/set', {
          name: this.templateName,
          key: this.templateKey,
          description: this.templateDescription,
          personType: this.get('templatePersonType.value'),
          sequence: this.templateSequence,
        });

        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.templateId;
        this.store.findRecord('people-schema/set', id).then((template) => {
          template.set('name', this.templateName);
          template.set('key', this.templateKey);
          template.set('description', this.templateDescription);
          template.set('personType', this.get('templatePersonType.value'));

          resolve(template);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          this.templateDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
          this.get('model.templates').update();
        });
    });
  }

  @action
  deleteTemplate(id) {
    this.errorMessage = '';
    this.store.findRecord('people-schema/set', id).then((template) => {
      this.templateName = template.get('name');
    });
    this.templateId = id;
    this.deleteTemplateDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let template = this.store.peekRecord(
          'people-schema/set',
          this.templateId,
        );

        // Delete record (locally + pushed to server).
        return template
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            template.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteTemplateDialogOpen = false;
        this.templateId = '';
        this.templateKey = '';
        this.templateName = '';
        this.templatePersonType = 0;
        this.templateSequence = this.get('model.groups.length') + 1;
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      entity.save();
    });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    this.templateId = '';
    this.templateName = '';
    this.templateKey = '';
    this.templateDescription = '';
    this.templatePersonType = 0;
    this.templateSequence = 9999;
  }
}
